import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics, siteConfig } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comic.relatedIds
    .map((rid) => comics.find((c) => c.id === rid))
    .filter(Boolean) as typeof comics;

  const formattedViews = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();
  const pageTitle = `${comic.title} - ${siteConfig.name}`;
  const pageDesc = `${comic.title}：${comic.description.slice(0, 120)}。${comic.status}，共${comic.chapters}话，评分${comic.rating}分。${siteConfig.name}免费在线观看。`;

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: pageTitle,
            description: pageDesc,
            url: `${siteConfig.url}/article/${comic.id}`,
            author: { '@type': 'Person', name: comic.author },
            datePublished: '2026-01-01',
            dateModified: comic.updateDate,
            publisher: { '@type': 'Organization', name: siteConfig.name, url: siteConfig.url },
          }),
        }}
      />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: `${comic.category}韩漫`, path: `/category/${comic.category === '热血' ? 'action' : comic.category === '恋爱' ? 'romance' : comic.category === '奇幻' ? 'fantasy' : comic.category === '悬疑' ? 'thriller' : 'comedy'}` },
            { label: comic.title },
          ]}
        />

        <article className="max-w-5xl mx-auto px-4 md:px-6 pb-10 md:pb-14">
          <div className="flex flex-col lg:flex-row gap-8 mb-10">
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden border border-background-200">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - ${siteConfig.name}`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="bg-secondary-100 text-secondary-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  {comic.category}
                </span>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {comic.title}
              </h1>

              <p className="text-sm text-foreground-600 mb-4">
                作者：<strong>{comic.author}</strong>
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-5 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-accent-500 font-bold text-lg">{comic.rating}</span>
                  <span className="text-foreground-500">分</span>
                  <div className="flex text-accent-400 ml-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className={`text-xs ${star <= Math.round(comic.rating / 2) ? 'ri-star-fill' : 'ri-star-line'}`}></i>
                    ))}
                  </div>
                </div>
                <span className="text-foreground-400">|</span>
                <span className="text-foreground-500 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {formattedViews}次阅读
                </span>
                <span className="text-foreground-400">|</span>
                <span className="text-foreground-500">共 {comic.chapters} 话</span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs bg-background-100 text-foreground-600 px-2.5 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button className="flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap">
                  <i className="ri-play-circle-line"></i> 开始阅读
                </button>
                <button className="flex items-center justify-center gap-2 bg-secondary-100 hover:bg-secondary-200 text-secondary-700 px-6 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 加入收藏
                </button>
              </div>
            </div>
          </div>

          <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6 mb-10">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">作品简介</h2>
            <p className="text-sm text-foreground-700 leading-relaxed">{comic.description}</p>
          </div>

          {relatedComics.length > 0 && (
            <div>
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">相关推荐</h2>
              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5"
                data-product-shop=""
              >
                {relatedComics.map((r) => (
                  <ComicCard
                    key={r.id}
                    id={r.id}
                    title={r.title}
                    cover={r.cover}
                    rating={r.rating}
                    category={r.category}
                    chapters={r.chapters}
                    status={r.status}
                    views={r.views}
                    tags={r.tags}
                  />
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}